"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const treatyAttributes = [{
  label: "Market reach",
  value: "Strategic representation across automotive, metal fabrication, and industrial sectors."
}, {
  label: "Sales methodology",
  value: "Technical, data-driven approach to lead generation and account management."
}, {
  label: "Engagement cycle",
  value: "Long-term partnership model with quarterly business reviews and growth acceleration."
}, {
  label: "Best suited for",
  value: "Manufacturers seeking high-volume, consistent market penetration and brand authority."
}, {
  label: "Typical retention",
  value: "Defined by multi-year service agreements and shared growth objectives."
}];
const facultativeAttributes = [{
  label: "Market reach",
  value: "Targeted, high-impact campaigns for specialized, non-standard industrial projects."
}, {
  label: "Sales methodology",
  value: "Bespoke, project-based outreach tailored to unique technical specifications."
}, {
  label: "Engagement cycle",
  value: "Per-project negotiation, with terms set at the moment of technical requirement."
}, {
  label: "Best suited for",
  value: "Complex, high-value, or custom-engineered solutions requiring specialized expertise."
}, {
  label: "Typical retention",
  value: "Bespoke per engagement, calibrated to the unique project scope and complexity."
}];
export default function Specialization() {
  const intro = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const leftCol = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const rightCol = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="15838" id="market-specialization" className="bg-background text-foreground py-24 md:py-32 px-6 md:px-10 lg:px-16">
      <div className="max-w-[1280px] mx-auto">
        {}
        <div ref={intro.ref} className={`max-w-2xl mx-auto text-center transition-all duration-700 ease-out ${intro.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)"
      }}>
          
          <h2 className="font-default font-semibold text-foreground" style={{
          fontSize: "clamp(2rem, 2.2vw + 1rem, 3.25rem)",
          letterSpacing: "-0.018em",
          lineHeight: 1.08
        }}>
            Standardized or bespoke — the answer is in the{" "}
            <em className="italic font-normal text-primary">technical requirement</em>
          </h2>
          <div className="h-px w-16 mx-auto mt-6" style={{
          backgroundColor: "var(--accent)"
        }} aria-hidden="true" />
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-12 mt-20 md:mt-24 relative">
          {}
          <div ref={leftCol.ref} data-index={0} className={`md:col-span-6 md:pr-12 lg:pr-16 transition-all duration-700 ease-out ${leftCol.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
          transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)"
        }}>
            <p className="font-mono text-xs uppercase mb-5 text-primary" style={{
            color: "var(--accent)",
            letterSpacing: "0.04em"
          }}>
              {"MODEL A · CORE PARTNERSHIP"}
            </p>
            <h3 className="font-default font-semibold text-foreground mb-6" style={{
            fontSize: "2rem",
            letterSpacing: "-0.018em",
            lineHeight: 1.1
          }}>
              Core Sales Representation
            </h3>
            <p className="font-default font-normal text-foreground/85" style={{
            fontSize: "clamp(1.125rem, 0.4vw + 1rem, 1.3125rem)",
            lineHeight: 1.55
          }}>
              A standing agreement to represent your entire product line, our core model builds market authority at the portfolio level. Predictable, scalable, and built for manufacturers who need sustained growth across recurring industrial sectors.
            </p>

            <div className="h-px w-16 my-10" style={{
            backgroundColor: "var(--border)"
          }} aria-hidden="true" />

            <dl className="space-y-4">
              {treatyAttributes.map((attr, i) => <div data-index={i} key={attr.label} className="grid grid-cols-1 gap-1.5">
                  <dt className="font-default font-medium uppercase text-accent-foreground" style={{
                fontSize: "0.8125rem",
                letterSpacing: "0.04em",
                color: "var(--accent)"
              }}>
                    {attr.label}
                  </dt>
                  <dd className="font-default font-normal text-foreground/90" style={{
                fontSize: "0.9375rem",
                lineHeight: 1.55
              }}>
                    {attr.value}
                  </dd>
                </div>)}
            </dl>

            <div className="mt-10">
              <Link to="/ServicesPage" className="inline-flex items-center gap-2 font-default text-foreground hover:text-[var(--accent)] transition-colors duration-200" style={{
              textUnderlineOffset: "6px",
              textDecoration: "underline",
              textDecorationThickness: "1px",
              textDecorationColor: "var(--accent)"
            }}>
                <span>Read full core briefing</span>
                <ArrowRight className="size-4" />
              </Link>
            </div>
          </div>

          {}
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px" style={{
          backgroundColor: "var(--border)"
        }} aria-hidden="true" />

          {}
          <div className="md:hidden h-px w-full my-12" style={{
          backgroundColor: "var(--border)"
        }} aria-hidden="true" />

          {}
          <div ref={rightCol.ref} data-index={1} className={`md:col-span-6 md:pl-12 lg:pl-16 transition-all duration-700 ease-out ${rightCol.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
          transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)",
          transitionDelay: rightCol.isVisible ? "100ms" : "0ms"
        }}>
            <p className="font-mono text-xs uppercase mb-5" style={{
            color: "var(--accent)",
            letterSpacing: "0.04em"
          }}>
              {"MODEL B · PROJECT SPECIFIC"}
            </p>
            <h3 className="font-default font-semibold text-foreground mb-6" style={{
            fontSize: "2rem",
            letterSpacing: "-0.018em",
            lineHeight: 1.1
          }}>
              Project-Based Sales
            </h3>
            <p className="font-default font-normal text-foreground/85" style={{
            fontSize: "clamp(1.125rem, 0.4vw + 1rem, 1.3125rem)",
            lineHeight: 1.55
          }}>
              Executed one project at a time, our project-based model is the instrument of precision. It exists for specialized exposures that fall outside standard parameters — the singular, the oversized, the structurally unusual — and demands bespoke analysis on every placement.
            </p>

            <div className="h-px w-16 my-10" style={{
            backgroundColor: "var(--border)"
          }} aria-hidden="true" />

            <dl className="space-y-4">
              {facultativeAttributes.map((attr, i) => <div data-index={i} key={attr.label} className="grid grid-cols-1 gap-1.5">
                  <dt className="font-default font-medium uppercase" style={{
                fontSize: "0.8125rem",
                letterSpacing: "0.04em",
                color: "var(--accent)"
              }}>
                    {attr.label}
                  </dt>
                  <dd className="font-default font-normal text-foreground/90" style={{
                fontSize: "0.9375rem",
                lineHeight: 1.55
              }}>
                    {attr.value}
                  </dd>
                </div>)}
            </dl>

            <div className="mt-10">
              <Link to="/ServicesPage" className="inline-flex items-center gap-2 font-default text-foreground hover:text-[var(--accent)] transition-colors duration-200" style={{
              textUnderlineOffset: "6px",
              textDecoration: "underline",
              textDecorationThickness: "1px",
              textDecorationColor: "var(--accent)"
            }}>
                <span>Read full project briefing</span>
                <ArrowRight className="size-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}